<template>
  <section class="pedidos" :aria-label="$t('clan.pedidos.titulo')">
    <p class="pedidos__rotulo">
      {{ $t('clan.pedidos.titulo') }}
      <button type="button" class="pedidos__alternar" @click="alternarFormulario">
        {{ formularioAberto ? $t('clan.pedidos.fechar') : $t('clan.pedidos.pedir') }}
      </button>
    </p>

    <!-- ======================== abrir um pedido ======================== -->
    <form v-if="formularioAberto" class="pedidos__form" @submit.prevent="pedir">
      <!--
        Uma LINHA por coisa, com o "mais" embaixo. O pedido é um só — quem lê o
        varal vê "o Alan precisa de três coisas" e escolhe o que dar.
      -->
      <div v-for="(linha, i) in rascunho" :key="linha.chave" class="pedidos__linha-form">
        <select
          v-model="linha.tipo"
          class="pedidos__campo"
          :aria-label="$t('clan.pedidos.tipo')"
          @change="trocouDeTipo(linha)"
        >
          <option v-for="tipo in TIPOS" :key="tipo" :value="tipo">
            {{ $t(`clan.pedidos.tipos.${tipo}`) }}
          </option>
        </select>

        <!--
          Moeda é valor puro; as outras seis escolhem no catálogo. Um select com
          "ouro" dentro pediria o id do ouro, que não existe.

          E o catálogo abre num SELETOR COM ARTE, não num `<select>`: o nativo
          não desenha imagem, e a lista de material são cinquenta nomes — quem
          nunca viu "Âmbar Congelado" não tem como saber o que está pedindo.
        -->
        <button
          v-if="!ehMoeda(linha.tipo)"
          type="button"
          class="pedidos__alvo crescer"
          :aria-label="$t('clan.pedidos.oQue')"
          @click="abrirSeletor(linha)"
        >
          <img
            v-if="escolhido(linha)?.imagem"
            class="pedidos__alvo-arte"
            :src="escolhido(linha).imagem"
            alt=""
          />
          <FishIcon v-else nome="search" tamanho="pequeno" />
          <span class="crescer">
            {{ escolhido(linha)?.nome || $t('clan.pedidos.escolha') }}
          </span>
          <FishIcon nome="chevron-down" tamanho="miudo" />
        </button>
        <span v-else class="pedidos__moeda crescer">
          <FishIcon :nome="linha.tipo === 'gold' ? 'coins' : 'gem'" tamanho="pequeno" />
          {{ $t('clan.pedidos.quanto') }}
        </span>

        <!--
          Vara e apetrecho não empilham — `player_gear` é uma linha por
          instância —, então o pedido é sempre de uma. Um campo travado em 1
          seria oferecer uma escolha que não existe.
        -->
        <input
          v-if="!ehInstancia(linha.tipo)"
          v-model.number="linha.quantidade"
          type="number"
          min="1"
          :max="teto(linha.tipo)"
          class="pedidos__campo pedidos__quantidade t-numero"
          :aria-label="$t('clan.pedidos.quantas')"
        />

        <button
          v-if="rascunho.length > 1"
          type="button"
          class="pedidos__tirar"
          :title="$t('clan.pedidos.tirarLinha')"
          @click="tirarLinha(i)"
        >
          <FishIcon nome="times" tamanho="miudo" />
        </button>
      </div>

      <button
        v-if="rascunho.length < ITENS_POR_PEDIDO"
        type="button"
        class="pedidos__mais"
        @click="adicionarLinha"
      >
        <FishIcon nome="plus" tamanho="miudo" />
        {{ $t('clan.pedidos.maisUma') }}
      </button>

      <FishButton
        variante="primario"
        bloco
        :desabilitado="!rascunhoValido || pedindo || semVaga"
        :motivo="motivoDeNaoPedir"
        :carregando="pedindo"
        @click="pedir"
      >
        {{ $t('clan.pedidos.enviarPedido') }}
      </FishButton>

      <p class="pedidos__nota">{{ $t('clan.pedidos.nota', { h: HORAS_DO_PEDIDO }) }}</p>
    </form>

    <!-- ========================== o varal ============================== -->
    <EmptyState
      v-if="!itens.length"
      icone="hand-holding-usd"
      :titulo="$t('clan.pedidos.vazio')"
      :explicacao="$t('clan.pedidos.vazioExplicacao')"
    />

    <ul v-else class="pedidos__lista">
      <li v-for="pedido in itens" :key="pedido.id" class="pedidos__pedido">
        <p class="pedidos__quem">
          {{
            pedido.seu
              ? $t('clan.pedidos.seuPedido')
              : $t('clan.pedidos.pedidoPor', { quem: pedido.quem || $t('clan.quadro.alguem') })
          }}
          <span class="pedidos__prazo">{{ prazo(pedido.expiraEm) }}</span>

          <!--
            O dono não doa para si — o servidor recusa, mas um botão que só
            existe para ser recusado é defeito, não proteção. No lugar dele,
            cancelar, e ele cancela o pedido INTEIRO: cancelar item por item
            deixaria cabeçalhos vazios pendurados no varal.
          -->
          <FishButton
            v-if="pedido.seu"
            tamanho="pequeno"
            variante="perigo"
            :desabilitado="agindo === `p${pedido.id}`"
            :carregando="agindo === `p${pedido.id}`"
            @click="cancelar(pedido)"
          >
            {{ $t('clan.pedidos.cancelar') }}
          </FishButton>
        </p>

        <ul class="pedidos__coisas">
          <li v-for="coisa in pedido.itens" :key="coisa.id" class="pedidos__coisa">
            <!--
              A arte vem do servidor (`image_media_id`), e não de um mapa de
              nomes no cliente: `itemImages.js` conhece 59 nomes — vara, barco,
              isca, casa e premium — e material, poção e apetrecho nasceram com
              a arte no banco. Isca e vara são a exceção: não têm mídia, e a
              arte delas vem de `itemImages.js` (ver `arteDoPedido.js`).
            -->
            <img
              v-if="coisa.alvo?.imagem"
              class="pedidos__arte"
              :src="coisa.alvo.imagem"
              :alt="nomeDe(coisa)"
            />
            <FishIcon
              v-else
              :nome="iconeDe(coisa)"
              tamanho="grande"
              class="pedidos__arte"
            />

            <div class="crescer">
              <p class="pedidos__nome">{{ nomeDe(coisa) }}</p>
              <p class="pedidos__estado t-numero">
                {{ formato(coisa.atendidas) }} / {{ formato(coisa.pedidas) }}
              </p>
            </div>

            <FishButton
              v-if="!pedido.seu"
              tamanho="pequeno"
              :desabilitado="coisa.vocePode < 1 || agindo === `i${coisa.id}`"
              :motivo="motivoDeNaoDoar(coisa)"
              :carregando="agindo === `i${coisa.id}`"
              @click="doar(pedido, coisa)"
            >
              {{ $t('clan.pedidos.doar') }}
            </FishButton>
          </li>
        </ul>
      </li>
    </ul>

    <p v-if="erro" class="pedidos__erro">{{ erro }}</p>

    <!--
      O SELETOR. Grade de encaixes com a arte, como a loja — e com busca, porque
      cinquenta encaixes sem filtro são a mesma parede de nomes de antes, só que
      colorida.
    -->
    <FishModal
      :aberto="!!seletor.linha"
      :titulo="seletor.linha ? $t(`clan.pedidos.tipos.${seletor.linha.tipo}`) : ''"
      @fechar="fecharSeletor"
    >
      <div class="pedidos__seletor">
        <input
          ref="busca"
          v-model="seletor.busca"
          type="search"
          class="pedidos__busca"
          :placeholder="$t('clan.pedidos.buscar')"
          :aria-label="$t('clan.pedidos.buscar')"
        />

        <p v-if="!encontrados.length" class="pedidos__nada">{{ $t('clan.pedidos.nadaEncontrado') }}</p>

        <FishGrid v-else :total="encontrados.length" :colunas-telefone="4" :colunas-desktop="6">
          <FishSlot
            v-for="item in encontrados"
            :key="item.id"
            :item="item"
            :selecionado="seletor.linha && seletor.linha.alvoId === item.id"
            @selecionar="escolher(item)"
          />
        </FishGrid>
      </div>
    </FishModal>
  </section>
</template>

<script>
import { FishButton, FishGrid, FishIcon, FishModal, FishSlot, EmptyState } from '@/shared/ui';
import * as v2 from '@/core/services/api/v2';
import { catalogoComArte, pedidosComArte } from '@/shared/utils/arteDoPedido';

/**
 * O VARAL DE PEDIDOS (D22, proposta 3 — 7.4k).
 *
 * É a outra metade do que a 1.1 acrescenta de *presença*: a meta da semana é
 * cooperação sem rosto, e o pedido é uma obrigação leve **entre duas pessoas**.
 * A pesquisa aponta essa como a mecânica social mais forte do gênero, e no
 * Fishbay ela é natural — pescador empresta apetrecho.
 *
 * Nada aqui é criado: doar é **transferência**. A isca sai de quem doa, a vara
 * troca de dono com a durabilidade que tem, o ouro passa pelo livro-caixa.
 *
 * ## O que mudou em 09/09/2026
 *
 * **Um pedido, várias coisas** (até cinco), e não só isca e vara: ouro,
 * Parallelium, poção, material, apetrecho e item de explorador entraram. O
 * cabeçalho é o que a outra pessoa lê como UM pedido; as linhas são o que se
 * atende, uma de cada vez.
 *
 * **Quem sabe o que você pode doar é o servidor.** Esta tela buscava dois
 * inventários por visita ao cais e reimplementava "a vara equipada não conta" —
 * a mesma regra em dois lugares, que é como `luck` ficou 7,5 no backend e 4,5
 * aqui. Agora cada linha chega com `vocePode`, e o botão só lê o número.
 */

/*
 * Espelham `packages/domain/src/clas-1-1.ts`. Aqui são rótulo e limite de
 * campo, não regra: quem decide é o servidor, que apara o que passar. Um número
 * frouxo aqui gera uma aparada entendível; a regra duplicada é que seria o
 * problema.
 */
const HORAS_DO_PEDIDO = 48;
const PEDIDOS_ABERTOS_POR_MEMBRO = 2;
const ITENS_POR_PEDIDO = 5;
const UNIDADES_POR_PEDIDO = 20;
const TETO_DE_OURO = 50_000;
const TETO_DE_PARALLELIUM = 100;

/** A ordem da lista é a da tela: o que se pede mais primeiro. */
const TIPOS = ['bait', 'rod', 'gold', 'parallelium', 'potion', 'material', 'fishingGear', 'explorerItem'];
const MOEDAS = ['gold', 'parallelium'];
const INSTANCIAS = ['rod', 'fishingGear'];

let proximaChave = 1;

export default {
  name: 'ClanPedidos',
  components: { FishButton, FishGrid, FishIcon, FishModal, FishSlot, EmptyState },
  emits: ['erro', 'mudou'],
  data() {
    return {
      HORAS_DO_PEDIDO,
      ITENS_POR_PEDIDO,
      TIPOS,
      itens: [],
      formularioAberto: false,
      /** As linhas em edição. `chave` é do `v-for`: o índice reordenaria ao tirar. */
      rascunho: [],
      catalogo: null,
      /** Qual linha está escolhendo, e o filtro da busca. */
      seletor: { linha: null, busca: '' },
      pedindo: false,
      /** `p<id>` para o cancelar do pedido, `i<id>` para o doar de uma linha. */
      agindo: null,
      erro: '',
    };
  },
  computed: {
    /** Quantos pedidos abertos são seus. O teto é por membro, não por clã. */
    semVaga() {
      return this.itens.filter((p) => p.seu).length >= PEDIDOS_ABERTOS_POR_MEMBRO;
    },
    rascunhoValido() {
      return (
        this.rascunho.length > 0 &&
        this.rascunho.every((l) => this.ehMoeda(l.tipo) || Boolean(l.alvoId))
      );
    },
    /**
     * O que a busca deixa passar.
     *
     * Sem acento e sem caixa: quem procura "ambar" tem que achar "Âmbar
     * Congelado" — exigir o circunflexo num campo de busca é exigir que a
     * pessoa saiba escrever o nome que ela está tentando descobrir.
     */
    encontrados() {
      if (!this.seletor.linha) return [];
      const lista = this.catalogoDoTipo(this.seletor.linha.tipo);
      const busca = this.semAcento(this.seletor.busca);
      if (!busca) return lista;
      return lista.filter((i) => this.semAcento(i.nome).includes(busca));
    },
    /** O que falta, escrito NO BOTÃO (D11) — nunca um botão morto sem motivo. */
    motivoDeNaoPedir() {
      if (this.semVaga) {
        return this.$t('clan.pedidos.tetoDePedidos', { n: PEDIDOS_ABERTOS_POR_MEMBRO });
      }
      if (!this.rascunhoValido) return this.$t('clan.pedidos.escolha');
      return '';
    },
  },
  created() {
    void this.carregar();
  },
  methods: {
    ehMoeda(tipo) {
      return MOEDAS.includes(tipo);
    },

    ehInstancia(tipo) {
      return INSTANCIAS.includes(tipo);
    },

    teto(tipo) {
      if (tipo === 'gold') return TETO_DE_OURO;
      if (tipo === 'parallelium') return TETO_DE_PARALLELIUM;
      return UNIDADES_POR_PEDIDO;
    },

    catalogoDoTipo(tipo) {
      return this.catalogo?.[tipo] || [];
    },

    /** O item que a linha escolheu, do catálogo — para desenhar arte e nome. */
    escolhido(linha) {
      if (!linha.alvoId) return null;
      return this.catalogoDoTipo(linha.tipo).find((i) => i.id === linha.alvoId) || null;
    },

    semAcento(texto) {
      return String(texto || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    },

    async abrirSeletor(linha) {
      this.seletor = { linha, busca: '' };
      // O catálogo já foi buscado ao abrir o formulário; esta guarda cobre o
      // caso de ele ter falhado lá e a pessoa tentar de novo aqui.
      await this.abrirCatalogo();
      // O foco vai para a busca: com cinquenta materiais, digitar é mais rápido
      // que rolar, e quem usa teclado não precisa caçar o campo.
      await this.$nextTick();
      this.$refs.busca?.focus();
    },

    fecharSeletor() {
      this.seletor = { linha: null, busca: '' };
    },

    escolher(item) {
      if (this.seletor.linha) this.seletor.linha.alvoId = item.id;
      this.fecharSeletor();
    },

    async carregar() {
      try {
        const r = await v2.pedidosDoCla(this.$store.state.player.id);
        this.itens = pedidosComArte(r.itens);
      } catch (error) {
        if (error?.response?.status !== 404) this.$emit('erro', error);
      }
    },

    /** Os seis catálogos, uma vez só — o formulário troca de tipo sem ir ao servidor. */
    async abrirCatalogo() {
      if (this.catalogo) return;
      try {
        this.catalogo = catalogoComArte(await v2.catalogoDePedido(this.$store.state.player.id));
      } catch (error) {
        this.$emit('erro', error);
      }
    },

    linhaNova() {
      return { chave: proximaChave++, tipo: 'bait', alvoId: null, quantidade: 5 };
    },

    async alternarFormulario() {
      this.erro = '';
      this.formularioAberto = !this.formularioAberto;
      if (!this.formularioAberto) return;
      this.rascunho = [this.linhaNova()];
      await this.abrirCatalogo();
    },

    adicionarLinha() {
      if (this.rascunho.length >= ITENS_POR_PEDIDO) return;
      this.rascunho.push(this.linhaNova());
    },

    tirarLinha(i) {
      this.rascunho.splice(i, 1);
    },

    /**
     * O alvo é de outra lista agora, e a quantidade é de outra escala.
     *
     * Mantê-los mandaria um id de isca no campo de poção, e 5.000 de ouro
     * viraria 5.000 iscas na troca de volta — que o servidor apara para 20, mas
     * o jogador só descobre depois de pendurar.
     */
    trocouDeTipo(linha) {
      linha.alvoId = null;
      linha.quantidade = this.ehMoeda(linha.tipo) ? 1_000 : 5;
    },

    nomeDe(coisa) {
      return coisa.alvo?.nome || this.$t(`clan.pedidos.tipos.${coisa.tipo}`);
    },

    /**
     * O ícone de quem não tem arte: as duas moedas, e o item de catálogo cuja
     * mídia ainda não foi cadastrada. Um encaixe vazio não diz nada; um ícone
     * genérico do tipo pelo menos diz "isto é uma poção".
     */
    iconeDe(coisa) {
      if (coisa.tipo === 'gold') return 'coins';
      if (coisa.tipo === 'parallelium') return 'gem';
      return 'box';
    },

    formato(valor) {
      return new Intl.NumberFormat('pt-BR').format(valor || 0);
    },

    /** O que falta, escrito NO BOTÃO (D11). O número vem do servidor. */
    motivoDeNaoDoar(coisa) {
      if (coisa.vocePode > 0) return '';
      if (this.ehMoeda(coisa.tipo)) return this.$t(`clan.pedidos.semSaldo.${coisa.tipo}`);
      if (this.ehInstancia(coisa.tipo)) return this.$t('clan.pedidos.semSobrando');
      return this.$t('clan.pedidos.semItem');
    },

    /**
     * "faltam 6 h". Relativo, e não a data de vencimento: o prazo é de 48 h, e
     * "quinta às 14h" faria o jogador calcular quanto tempo isso é.
     */
    prazo(iso) {
      const horas = Math.floor((new Date(iso).getTime() - Date.now()) / 3600000);
      if (horas <= 0) return this.$t('clan.pedidos.vencendo');
      if (horas < 24) return this.$t('clan.pedidos.faltamHoras', { n: horas });
      return this.$t('clan.pedidos.faltamDias', { n: Math.floor(horas / 24) });
    },

    async pedir() {
      if (!this.rascunhoValido || this.semVaga) return;
      this.pedindo = true;
      this.erro = '';
      try {
        await v2.pedirApetrecho(
          this.$store.state.player.id,
          this.rascunho.map((l) => ({
            tipo: l.tipo,
            // Moeda não tem alvo, e mandar `null` é diferente de não mandar: o
            // contrato é `.strict()` e o servidor recusa um id ali.
            ...(this.ehMoeda(l.tipo) ? {} : { alvoId: l.alvoId }),
            quantidade: this.ehInstancia(l.tipo) ? 1 : l.quantidade || 1,
          })),
        );
        this.formularioAberto = false;
        this.rascunho = [];
        await this.carregar();
      } catch (error) {
        this.erro = error?.response?.data?.message || this.$t('clan.pedidos.falhou');
      } finally {
        this.pedindo = false;
      }
    },

    /**
     * Doa o que falta, até o que se tem.
     *
     * Uma unidade por clique faria quem quer ajudar clicar cinco vezes; doar
     * tudo esvaziaria o baú de quem tem uma caixa inteira. O `vocePode` já vem
     * do servidor como o mínimo entre o que falta e o que se tem.
     */
    async doar(pedido, coisa) {
      this.agindo = `i${coisa.id}`;
      this.erro = '';
      try {
        await v2.doarApetrecho(
          this.$store.state.player.id,
          pedido.id,
          coisa.id,
          coisa.vocePode,
        );
        await this.carregar();
        // O baú do doador mudou: a isca saiu, o ouro saiu, a vara trocou de dono.
        this.$emit('mudou');
      } catch (error) {
        this.erro = error?.response?.data?.message || this.$t('clan.pedidos.falhou');
      } finally {
        this.agindo = null;
      }
    },

    async cancelar(pedido) {
      this.agindo = `p${pedido.id}`;
      this.erro = '';
      try {
        await v2.cancelarPedido(this.$store.state.player.id, pedido.id);
        await this.carregar();
      } catch (error) {
        this.erro = error?.response?.data?.message || this.$t('clan.pedidos.falhou');
      } finally {
        this.agindo = null;
      }
    },
  },
};
</script>

<style scoped>
.pedidos {
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.pedidos__rotulo {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

.pedidos__alternar {
  margin-left: auto;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: var(--f-1);
  color: var(--latao);
  text-decoration: underline dashed;
  cursor: pointer;
}

/* ------------------------------------------------------------- formulário */

.pedidos__form {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-2);
}

.pedidos__linha-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--e-1);
}

.pedidos__campo {
  padding: var(--e-1);
  border: 2px solid var(--madeira);
  background: var(--papel);
  color: var(--tinta);
  font: inherit;
  min-width: 0;
}

.pedidos__quantidade {
  width: 90px;
  text-align: center;
}

/* O campo que abre o seletor: parece campo, é botão. */
.pedidos__alvo {
  display: flex;
  align-items: center;
  gap: var(--e-1);
  min-width: 0;
  padding: var(--e-1);
  border: 2px solid var(--madeira);
  background: var(--papel);
  color: var(--tinta);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.pedidos__alvo > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pedidos__alvo-arte {
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex: 0 0 auto;
}

@media (hover: hover) {
  .pedidos__alvo:hover {
    border-color: var(--latao);
  }
}

/* ---------------------------------------------------------------- seletor */

.pedidos__seletor {
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.pedidos__busca {
  padding: var(--e-2);
  border: 2px solid var(--madeira);
  background: var(--papel-luz);
  color: var(--tinta);
  font: inherit;
}

.pedidos__nada {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  font-style: italic;
}

/* Moeda ocupa o lugar do select: sem isso o campo de valor pula para a
 * esquerda quando o tipo troca, e a linha inteira dança. */
.pedidos__moeda {
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.pedidos__mais,
.pedidos__tirar {
  background: none;
  border: 0;
  padding: var(--e-1);
  font: inherit;
  font-size: var(--f-1);
  color: var(--latao);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
}

.pedidos__mais {
  align-self: flex-start;
  text-decoration: underline dashed;
}

.pedidos__tirar {
  flex: 0 0 auto;
  color: var(--tinta-fraca);
}

@media (hover: hover) {
  .pedidos__tirar:hover {
    color: var(--ruim, #a33);
  }
}

.pedidos__nota {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

/* ------------------------------------------------------------------ varal */

.pedidos__lista {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

/* Cada PEDIDO é um bloco, e as coisas dele ficam recuadas por dentro: é o que
 * faz ler como "o Alan precisa de três coisas" e não como seis pedidos soltos. */
.pedidos__pedido {
  border-bottom: 1px dashed var(--papel-sombra);
  padding-bottom: var(--e-2);
}

.pedidos__pedido:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.pedidos__quem {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  margin: 0 0 var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.pedidos__prazo {
  margin-right: auto;
}

.pedidos__coisas {
  list-style: none;
  margin: 0;
  padding: 0 0 0 var(--e-2);
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  border-left: 3px solid var(--papel-sombra);
}

.pedidos__coisa {
  display: flex;
  align-items: center;
  gap: var(--e-2);
}

.pedidos__arte {
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex: 0 0 auto;
}

.pedidos__nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta);
}

.pedidos__estado {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.pedidos__erro {
  margin: 0;
  font-size: var(--f-1);
  color: var(--ruim, #a33);
}
</style>
